import { PayOverTimeAutopayResultTypenames } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useSetAutopayForPayOverTime } from 'features/payOverTime/hooks/useSetAutopayForPayOverTime';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

function Autopay() {
  const router = useRouter();
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'autopayScreen',
  });
  const { bottom } = useSafeAreaInsets();
  const { autoPayEnabled, loanId } = useLocalSearchParams<{
    autoPayEnabled?: string;
    loanId?: string;
  }>();
  const [isAutopayEnabled, setIsAutopayEnabled] = useState(
    autoPayEnabled === 'true',
  );

  const { errorModel, fetching, setAutopay } = useSetAutopayForPayOverTime();

  const handleToggleAutopay = async () => {
    if (!loanId || fetching) {
      return;
    }

    const result = await setAutopay({
      loanId,
      enabled: !isAutopayEnabled,
    });

    if (
      result?.__typename ===
      PayOverTimeAutopayResultTypenames.PayOverTimeAutopaySuccess
    ) {
      setIsAutopayEnabled(result.autoPayEnabled);
    }
  };

  if (errorModel) {
    return (
      <View style={styles.container}>
        <Stack.Screen
          options={{
            title: translatedText('header'),
            headerBackVisible: true,
            headerShown: true,
            gestureEnabled: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} />
            ),
          }}
        />
        <ScreenTransitionOverlay
          isLoading={fetching}
          LoadingComponent={<FullScreenLoading />}
          ErrorComponent={
            errorModel ? (
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={errorModel.messageHeadline}
                messageBody={errorModel.messageBody}
                primaryButton={{
                  title: translatedText('error.button'),
                  onPress: () => router.back(),
                }}
              />
            ) : null
          }
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText('header'),
          headerBackVisible: true,
          headerShown: true,
          gestureEnabled: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <TitleLgText numberOfLines={0} style={styles.title}>
          {translatedText(isAutopayEnabled ? 'onTitle' : 'offTitle')}
        </TitleLgText>
        <BodyBaseText numberOfLines={0} style={styles.body}>
          {translatedText(isAutopayEnabled ? 'onBody' : 'offBody')}
        </BodyBaseText>
      </ScrollView>
      <View
        style={[
          styles.buttonContainer,
          { paddingBottom: bottom + theme.spacing.m },
        ]}
      >
        <Button
          label={translatedText(isAutopayEnabled ? 'offButton' : 'onButton')}
          onPress={handleToggleAutopay}
          type={isAutopayEnabled ? 'SECONDARY' : 'PRIMARY'}
          isLoading={fetching}
          isDisabled={!loanId || fetching}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  body: {
    paddingHorizontal: theme.spacing.m,
  },
  buttonContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingTop: theme.spacing.m,
  },
  title: {
    paddingHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.s,
  },
});

export default Autopay;
